import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Icon, Modal } from '@successmode-ktp/kui';

import ICON_LOGO_WHITE from '@/assets/globalModal/icon_logo_white.png';
import { useMutation } from 'react-query';
import { createJandiMessage } from '@/pages/MedicalTourismApply/util/jandiMessageTemplate';
import { postJandiWebhook } from '@/pages/MedicalTourismApply/api/jandi';
import { useAppSelector } from '@/hooks/useReduxHooks';
interface MedicalAgreeFormData {
  hospitalName: string;
}

function GlobalModal() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const loginToken = localStorage.getItem('loginToken');
  const TARGET_ROOM_ID = '2c0e85178bb49d73b8aac312853275e4';
  const name = localStorage.getItem('name');

  const jandiMutation = useMutation(
    (formData: MedicalAgreeFormData) => {
      const payload = {
        title: '🏥 개인정보 수집 이용 안내 동의',
        description: `${formData.hospitalName}`,
        body: `${formData.hospitalName}`,
      };
      // target_room_id는 필요에 따라 변경하세요
      return postJandiWebhook(payload, TARGET_ROOM_ID);
    },
    {
      onSuccess: () => {
        console.log('Jandi 알림 전송 성공');
      },
      onError: (error) => {
        console.error('Jandi 알림 전송 실패:', error);
      },
    },
  );
  useEffect(() => {
    const hideAgreeNoticeUntil = localStorage.getItem('hideAgreeNoticeUntil');
    const agreedPermanently = localStorage.getItem('agreedToMedibook');
    const now = new Date().getTime();

    // 영구적으로 동의한 경우 모달을 표시하지 않음
    if (agreedPermanently === 'true') {
      setIsOpen(false);
      return;
    }

    // hideAgreeNoticeUntil이 있고, 현재 시간이 저장된 시간보다 이전이면 모달을 숨김
    if (hideAgreeNoticeUntil && now < parseInt(hideAgreeNoticeUntil)) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [location.pathname, loginToken]);

  const onHideForOneHour = () => {
    const now = new Date().getTime();
    const oneHourLater = now + 60 * 60 * 1000; // 1시간 후의 타임스탬프
    localStorage.setItem('hideAgreeNoticeUntil', oneHourLater.toString());
    setIsOpen(false);
  };

  const onAgree = () => {
    jandiMutation.mutate({ hospitalName: name ?? '병원명 누락' });
    localStorage.setItem('agreedToMedibook', 'true');
    setIsOpen(false);
  };

  return (
    <>
      <Modal isOpen={isOpen} hasBackdrop wrapperStyle={{ padding: '0' }}>
        <ModalContainer>
          <HeaderSection>
            <HeaderTitle>
              [KTP] 택스리펀 서비스 종료 및 외국인 환자 유치 서비스 안내
            </HeaderTitle>
            <CloseButton onClick={onHideForOneHour}>
              1시간 동안 보지않기
              <Icon.X size={20} fillColor='#BBBBBB' />
            </CloseButton>
          </HeaderSection>

          <BodySection>
            <NoticeContent>
              <Paragraph>
                안녕하세요. 택스리펀 서비스 KTP입니다.
                <br />
                KTP 택스리펀 서비스는 2025년 12월 31일부로 종료됩니다.
              </Paragraph>
              <Paragraph>
                택스리펀 서비스 종료 후에도 병원의 외국인 환자 유치 및 매출
                성장을 지원하기 위해,
                <br />
                KTP는 외국인 환자 유치 서비스 '메디북(MEDIBOOK)'을 제공합니다.
                <br />
                입점 동의하기를 눌러주시면 메디북 서비스 이용 및 입점과 관련된
                상세 안내가 순차적으로 제공됩니다.
              </Paragraph>
              <SmallText>
                *서비스 이용에 대한 별도의 이용료는 부과되지 않습니다.
              </SmallText>
              <Paragraph style={{ marginTop: '12px' }}>
                앞으로도 병원 운영에 도움이 되는 서비스 제공을 위해 항상 최선을
                다하겠습니다.
                <br />
                감사합니다.
              </Paragraph>

              <ContactInfo>
                <ContactText>
                  [개인정보 수집·이용 안내]
                  <br />
                  수집 항목: 병원명, 병원 내·외부 이미지, 연락처, 영업시간,
                  사업장 주소 등
                  <br />
                  수집 목적: 외국인 환자 유치 서비스(메디북) 운영
                </ContactText>
                <PhoneNumber>
                  문의 ☎️<Underline>02-6275-8011</Underline> (연결 2번)
                </PhoneNumber>
              </ContactInfo>
            </NoticeContent>

            <ButtonArea>
              <ConfirmButton onClick={onAgree}>동의하기</ConfirmButton>
            </ButtonArea>
          </BodySection>
        </ModalContainer>
      </Modal>
    </>
  );
}

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  width: 700px;
  position: relative;
  padding: 30px;
`;

const CloseButton = styled.button`
  cursor: pointer;
  transition: opacity 0.2s;
  color: #999999;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;

  /* WebTitle/14_SB */
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 24px;
    height: 24px;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background: #ffffff;
`;

const HeaderTitle = styled.h4`
  color: #111;
  font-size: 21px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 31.5px */
  letter-spacing: -0.42px;
`;

const BodySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const NoticeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Paragraph = styled.p`
  font-size: 15px;
  font-weight: 400;
  line-height: 1.6;
  color: #333333;
  margin: 0;
  letter-spacing: -0.3px;
`;

const SmallText = styled.p`
  margin-top: -8px;
  color: var(--KTP-Grayscale-Gray_08, #bbb);

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const ContactInfo = styled.div`
  margin-top: 20px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContactText = styled.p`
  color: #999;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const PhoneNumber = styled.p`
  margin-top: 16px;
  color: #999;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const ConfirmButton = styled.button`
  flex: 1;
  background-color: #246cf6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1856cd;
  }
`;

const Underline = styled.span`
  text-decoration: underline;
`;

export default GlobalModal;
